import React, { FC, ReactNode, useContext } from "react";
import { GoogleReCaptchaProvider } from "@google-recaptcha/react";

import { Context as ConfigContext } from "@app/provider/config";
import { Context as IntlContext } from "@app/provider/intlWrapper";

interface RecaptchaProviderProps {
  children: ReactNode;
}

/**
 * Wraps children in a v3 GoogleReCaptchaProvider so any descendant can use
 * useGoogleReCaptcha() to invoke executeV3(action).
 *
 * For v2 fallback (low-score retry), descendants render a standalone
 * <GoogleReCaptchaCheckbox> with the v2 site key — that component loads
 * its own v2 script independently of this v3 provider.
 *
 * Mount logic:
 * - Mounts when recaptcha is enabled in config AND a v3 site key is set.
 * - Becomes a pass-through otherwise. Consumers must defensively treat
 *   executeV3 as optional in that case.
 */
const RecaptchaProvider: FC<RecaptchaProviderProps> = ({ children }) => {
  const { config } = useContext(ConfigContext);
  const { currentLanguage } = useContext(IntlContext);

  const recaptcha = config.recaptcha;

  if (!recaptcha?.enabled || !recaptcha?.v3SiteKey) {
    return <>{children}</>;
  }

  return (
    <GoogleReCaptchaProvider
      type="v3"
      siteKey={recaptcha.v3SiteKey}
      language={currentLanguage}
    >
      {children}
    </GoogleReCaptchaProvider>
  );
};

export default RecaptchaProvider;
